import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import copy from 'fast-copy';
import _deepEqual from 'fast-deep-equal';
import type { Observable } from 'rxjs';

export const injectUntilDestroyed = () => {
  const op = takeUntilDestroyed();
  return <T>(source: Observable<T>) => <Observable<T>>source.pipe(op);
};

export const sortByKey = <T>(
  key: keyof T,
  options: { direction: 'desc' | 'asc'; modifyFn?: Function } = {
    direction: 'desc',
  },
) => {
  const direction = options.direction === 'desc' ? -1 : 1;
  return (a: T, b: T) => {
    const comparableA: T = options.modifyFn ? options.modifyFn(a[key]) : a[key];
    const comparableB: T = options.modifyFn ? options.modifyFn(b[key]) : b[key];
    return direction * (comparableA > comparableB ? 1 : comparableB > comparableA ? -1 : 0);
  };
};

export const uniqByKey = <T>(arr: T[], key: keyof T) => {
  const temp = new Set();
  return arr.filter(Boolean).filter(({ [key]: value }) => {
    if (temp.has(value)) return false;
    temp.add(value);
    return true;
  });
};

export const groupByKey = <T extends Record<string, any>, V extends keyof T>(
  list: T[],
  listElementKey: V,
): Record<T[V], T[]> =>
  (list || []).reduce(
    (groupMap, listElement: T) => {
      const groupByElementKey: V = listElement[listElementKey];
      const groupValues: T[] = groupMap[groupByElementKey] || [];
      return { ...groupMap, [groupByElementKey]: [...groupValues, listElement] };
    },
    <Record<V, T[]>>{},
  );

export const encodeHTML = (str: string): string => {
  const div = document.createElement('div');
  div.textContent = str;
  // eslint-disable-next-line no-restricted-properties
  return div.innerHTML;
};

export const decodeHTML = (() => {
  const textAreaElement = document.createElement('textarea');
  return (str: string): string => {
    // eslint-disable-next-line no-restricted-properties
    textAreaElement.innerHTML = str;
    return textAreaElement.childNodes[0]?.nodeValue || '';
  };
})();

export const snakeCase = (str: string) =>
  str
    .replace(/\W+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map(word => word.toLowerCase())
    .join('_');

export const titleCase = (str: string) =>
  str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const camelCase = (str: string) => {
  const chars = str.trim().split('');
  const [firstWord, ...words] = [chars[0].toLowerCase(), ...chars.slice(1)]
    .join('')
    .replace(/ /, '_')
    .split(/[_-]/)
    .filter(Boolean);

  return firstWord + words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
};

export const kebabCase = (str: string) =>
  str.split('').reduce((acc, char) => {
    const charCode = char.charCodeAt(0);
    const isUpperCaseChar = charCode > 64 && charCode < 91;

    return acc + (isUpperCaseChar ? `-${char.toLowerCase()}` : char);
  }, '');

export const tokenToCssVar = (token: string) => `var(--${kebabCase(token ?? '')})`;

export const includesWithTrim = (
  label: string,
  query: string,
  options: { trim: boolean } = { trim: true },
): boolean =>
  options.trim
    ? label.toLowerCase().includes(query.toLowerCase().trim())
    : label.toLowerCase().includes(query.toLowerCase());

export const getDifferences = <T>(arr1: T[], arr2: T[]) => arr1.filter(c => !arr2.includes(c));

export const getIntersection = <T>(arr1: T[], arr2: T[]) => arr1.filter(c => arr2.includes(c));

export const randomInt = (min: number, max: number) => {
  const lower = Math.ceil(Math.min(min, max));
  const upper = Math.floor(Math.max(min, max));

  return Math.floor(lower + Math.random() * (upper - lower + 1));
};

export const pickTruthy = <T extends object, V extends keyof T>(object: T) => {
  const entries = <Array<[V, T[V]]>>Object.entries(object);
  return entries.reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {});
};

export const isEmpty = <T>(value: T): value is T extends null | undefined ? T : never =>
  value === null || value === undefined || (<any>value)?.length === 0;

export const isEmptyObject = <T extends {}>(value: T): boolean => Object.keys(value).length === 0;

export const isString = <T>(value: T): boolean => typeof value === 'string';

export interface DebouncedFunction {
  (): void;
  destroy: () => void;
}

export const debounceAnimationFrame = <T extends Function>(func: T): DebouncedFunction => {
  let handle: number | null = null;
  const destroy = () => {
    cancelAnimationFrame(<number>handle);
    handle = null;
  };

  const debounced = <DebouncedFunction>function (this: any) {
    if (handle !== null) return;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;
    const args = arguments;
    handle = requestAnimationFrame(() => {
      func.apply(context, args);
      handle = null;
    });
  };
  debounced.destroy = destroy;
  return debounced;
};

export const deepClone = copy;
export const deepEqual = _deepEqual;

export const formatHTML = (html: string): string =>
  html
    .replace(/<\/p>/g, ' ')
    .replace(/&nbsp;/g, '')
    .replace(/(<([^>]+)>)/gi, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

export const mergeArraysInObjects = <T extends object>(obj1: T, obj2: T): Partial<T> =>
  Object.keys(obj2)
    .filter(key => Array.isArray((<any>obj2)[key]))
    .reduce(
      (prev, key) => ({
        ...prev,
        [key]: mergeArraysWithoutDuplicates((<any>obj1)[key], (<any>obj2)[key]),
      }),
      {},
    );

export const mergeArraysWithoutDuplicates = <T>(arr1: T[] = [], arr2: T[] = []) => [
  ...new Set([...arr1, ...arr2]),
];

export const omit = <T extends Record<PropertyKey, any>, P extends keyof T>(
  obj: T,
  ...keys: [P, ...P[]]
): Omit<T, P> => {
  const omitMap = <Record<P, true | undefined>>Object.fromEntries(keys.map(key => [key, true]));
  return <Omit<T, P>>Object.fromEntries(Object.entries(obj).filter(([key]) => !omitMap[<P>key]));
};

export const wrapToArray = <T>(item: T) =>
  <T extends any[] ? T : T[]>(Array.isArray(item) ? item : [item]);

/**
 * @desc Truncate any string containing graphemes. Works with all emojis
 */
export const truncateUnicodeString = (str: string, maxLength: number) =>
  [...new Intl.Segmenter().segment(str)]
    .slice(0, maxLength)
    .map(x => x.segment)
    .join('');

export const unicodeStringLength = (str: string): number =>
  [...new Intl.Segmenter().segment(str)].length;
